import React, { FC, ChangeEvent, useEffect, useMemo, useCallback } from "react"

import { InputField } from "src/components/common/inputfield"

import { FilterSortSelection } from "src/components/core/filterSortSelector/FilterSortSelector"
import { create } from "src/helpers/bem"
import { Category } from "src/states/shop/category"
import { useFilter, mergeFilter, FilterSorting } from "src/states/shop/filter"
import { useProducts, mergeProducts } from "src/states/shop/products"
import { TranslationMessages } from "src/translations"

import styles from "./ShopOverviewProductsHeader.module.scss"

const bem = create(styles, "ShopOverviewProductsHeader")

export type ShopOverviewProductsHeaderProps = {
  messages: TranslationMessages
}

export const productsSortingOptions = [
  "default",
  "favorite",
  "alphabeticUp",
  "alphabeticDown",
  "priceUp",
  "priceDown",
] as FilterSorting[]

export const ShopOverviewProductsHeader: FC<
  ShopOverviewProductsHeaderProps
> = ({ messages }) => {
  const m = messages.pages.shop.overview.header
  const { products, loading: loadingProducts } = useProducts()
  const { filterBy, search, sortBy } = useFilter()

  const isAllProductsCategory = (category: Category) =>
    category.categoryType === "category" && category.children === undefined

  const onFilter = useCallback(async () => {
    if (products.length === 0 || loadingProducts) return

    let result = [...products]

    if (
      filterBy?.category?.id &&
      !isAllProductsCategory(filterBy.category as Category)
    ) {
      result = result.filter((p) =>
        [p.categoryId, p.parentCategoryId].includes(filterBy.category?.id),
      )
    }

    result = result.filter(
      (p) =>
        p.name?.toLowerCase().includes(search.toLowerCase()) ||
        p.note?.toLowerCase().includes(search.toLowerCase()),
    )

    switch (sortBy) {
      case "alphabeticUp":
        result.sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""))
        break
      case "alphabeticDown":
        result.sort((a, b) => (b.name ?? "").localeCompare(a.name ?? ""))
        break
      case "priceUp":
        result.sort((a, b) => (a.price ?? 0) - (b.price ?? 0))
        break
      case "priceDown":
        result.sort((a, b) => (b.price ?? 0) - (a.price ?? 0))
        break
      case "favorite":
        result.sort((a, _) => (a.favorite ? -1 : 1))
        break
    }

    mergeProducts({
      filteredProducts: result.filter((p) => p.isValid),
    })
  }, [filterBy, products, search, sortBy, loadingProducts])

  // onFilter is intentionally excluded from deps: products changes are
  // handled by loadProducts in ShopOverview, the filter effect should only
  // run when the user changes filter criteria (filterBy, sortBy, search).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    onFilter()
  }, [filterBy, sortBy, search])

  const handleChangeSearch = (e: ChangeEvent<HTMLInputElement>) => {
    mergeFilter({
      search: e?.target?.value ?? "",
    })
  }

  const handleChangeSorting = (value: string) => {
    const sortingValue = value as FilterSorting
    mergeFilter({
      sortBy: sortingValue,
    })
  }

  const sortingItems = useMemo(
    () =>
      productsSortingOptions.map((key) => ({
        id: key,
        label: m.sorting.items?.[key],
      })),
    [m.sorting.items],
  )
  return (
    <div className={bem()}>
      {/* {filterBy?.category?.notice && (
        <div className={bem("content__wrapper")}>
          <Accordion
            id="info"
            label={m.info.title}
            classNames={{
              label: bem("accordion__label"),
            }}
          >
            <div className={bem("info")}>
              {filterBy?.category?.notice?.title && (
                <span className={bem("info__title")}>
                  {filterBy?.category?.notice.title}
                </span>
              )}
              {filterBy?.category?.notice?.content && (
                <span className={bem("info__content")}>
                  {filterBy?.category?.notice.content}
                </span>
              )}
            </div>
          </Accordion>
        </div>
      )} */}
      <div className={bem("toolbar")}>
        <div className={bem("search")}>
          <InputField
            label={m.search.input}
            name="search"
            sizeVariant="medium"
            type="text"
            value={search}
            classes={{
              root: bem("input"),
            }}
            onChange={handleChangeSearch}
          />
        </div>

        <FilterSortSelection
          options={sortingItems}
          sizeVariant="small"
          value={sortBy}
          onChange={handleChangeSorting}
        />
      </div>
    </div>
  )
}
